import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { from, switchMap } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import { environment } from '../../../environments/environment';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Skip auth for local static assets (e.g. i18n translation files)
  if (req.url.startsWith('/assets/') || req.url.includes('/assets/i18n/')) {
    return next(req);
  }

  // Only attach auth/cache-busting headers to our own backend API. Third-party integrations
  // (e.g. the GeoNames city lookup) don't expect these headers and reject them during the CORS
  // preflight (their Access-Control-Allow-Headers doesn't include Cache-Control/Pragma/etc.) —
  // legacy never hit this because it called GeoNames via the old @angular/http Http service,
  // which had no interceptor pipeline at all.
  const isOwnApiRequest = req.url.startsWith(environment.apiBaseUrl) || !/^https?:\/\//i.test(req.url);
  if (!isOwnApiRequest) {
    return next(req);
  }

  const authService = inject(AuthenticationService);
  const cacheBustingHeaders = req.headers
    .set('Cache-Control', 'no-cache')
    .set('Pragma', 'no-cache')
    .set('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT');

  // Do not set Content-Type for multipart/file uploads
  const baseHeaders =
    req.body instanceof FormData
      ? cacheBustingHeaders
      : cacheBustingHeaders.set('Content-Type', 'application/json');

  // Custom 36-char supplier token: type = chars 0-2, value = chars 3-35
  const customToken = authService.getCustomToken();
  if (customToken) {
    const tokenType = customToken.substring(0, 3);
    const tokenValue = customToken.substring(3);
    return next(req.clone({ headers: baseHeaders.set('Authorization', `${tokenType} ${tokenValue}`) }));
  }

  // Standard MSAL Bearer token
  return from(authService.acquireMsalToken()).pipe(
    switchMap(result => next(req.clone({ headers: baseHeaders.set('Authorization', `Bearer ${result.accessToken}`) }))),
  );
};
